import type { FMEAUpdateReview, ReviewSeat } from '@/versions/v1/schema/case';
import type { DiagnosticRule } from '@/versions/v1/schema/diagnostic-rule';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { Claim, Ruling } from '@/versions/v1/schema/claim';
import type { AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import { formatMonthDay } from '@/versions/v1/shared/clock.derive';
import { RULE_SET_VERSIONS, SEEDED_RULINGS } from '@/versions/v1/shared/bunker-hill-rule-claims';
import { ruleSetCounts, ruleSetDiff, liveRuleSet } from '@/versions/v1/shared/RuleSetEngine';

export type RuleSetChangeRow = {
  kind: 'added' | 'changed';
  label: string;
};

export type ApprovalsRuleSetPanel = {
  chip: string;
  fromVersion: string;
  toVersion: string;
  rows: RuleSetChangeRow[];
};

const REVIEW_SEAT_LABEL: Record<ReviewSeat, string> = {
  'senior-metallurgist': 'Senior Metallurgist',
  'senior-reliability-engineer': 'Senior Reliability Engineer',
};

function routedSeatAwaitingLabel(review: FMEAUpdateReview): string {
  const seat = review.reviewSeats[0];
  if (!seat) return 'awaiting review';
  return `routed to ${REVIEW_SEAT_LABEL[seat]} · awaiting review`;
}

/** The seeded rule set's version: the one an approved row is live in at t0. */
const SEEDED_RULE_SET_VERSION = RULE_SET_VERSIONS[RULE_SET_VERSIONS.length - 1]!.n;

/**
 * `liveVersion` is the rule set the approved row is live in — the live set's
 * version (polish.P28c, comp adjudication 8: one version on the glass; the row
 * used to type `rs-v8`, a version that does not exist while the chip read v7).
 */
export function rulingStateLabel(
  review: FMEAUpdateReview,
  cursor: SeriesTime,
  liveVersion: number = SEEDED_RULE_SET_VERSION,
): string {
  if (review.approvalStatus === 'approved' && review.approvedBy) {
    return `approved · live in rule set v${liveVersion}`;
  }
  if (review.approvalStatus === 'rejected') {
    return 'rejected';
  }
  const parts: string[] = [];
  if (review.reviewedBy && review.reviewStatus === 'approved') {
    parts.push(`reviewed by Met ${formatMonthDay(review.reviewedAt ?? '', cursor)}`);
    if (review.approvalStatus === 'pending') {
      parts.push(`awaiting ${review.approvalLabel ?? 'GM'}`);
    }
  } else if (review.reviewStatus === 'pending') {
    return routedSeatAwaitingLabel(review);
  }
  if (parts.length === 0) {
    return routedSeatAwaitingLabel(review);
  }
  return parts.join(' · ');
}

export function diffEntryLabel(
  claim: Claim<DiagnosticRule>,
  ruling: Ruling | undefined,
  cursor: SeriesTime,
): string {
  if (!ruling) return claim.valueLabel;
  const seat =
    ruling.author.role === 'gm'
      ? 'GM'
      : ruling.author.role === 'metallurgist'
        ? 'Met'
        : ruling.author.role;
  const date = formatMonthDay(ruling.decidedAt, cursor);
  return `${claim.valueLabel} · ${seat} ${date}`.trim();
}

export function approvalRuleSetChip(advisory: AdvisorySessionState): string {
  const { version } = liveRuleSet(advisory);
  const counts = ruleSetCounts(advisory);
  return `Rule set v${version.n} · ${counts.live} live · ${counts.proposed} proposed`;
}

export function approvalsRuleSetPanel(
  advisory: AdvisorySessionState,
  cursor: SeriesTime,
): ApprovalsRuleSetPanel {
  const diff = ruleSetDiff(advisory);
  const rulings = [...SEEDED_RULINGS, ...advisory.rulings];
  const rows: RuleSetChangeRow[] = [
    ...diff.added.map((claim) => {
      const ruling = rulings.find(
        (row) => row.acceptsClaimId === claim.id && row.stage === 'approval',
      );
      return {
        kind: 'added' as const,
        label: diffEntryLabel(claim as Claim<DiagnosticRule>, ruling, cursor),
      };
    }),
    ...diff.changed.map(({ after }) => {
      const ruling = rulings.find(
        (row) => row.acceptsClaimId === after.id && row.stage === 'approval',
      );
      return {
        kind: 'changed' as const,
        label: diffEntryLabel(after as Claim<DiagnosticRule>, ruling, cursor),
      };
    }),
  ];
  return {
    chip: approvalRuleSetChip(advisory),
    fromVersion: diff.from.replace('rs-v', ''),
    toVersion: diff.to.replace('rs-v', ''),
    rows,
  };
}
